import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { supabase } from "@/shared/auth/supabase";

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: () => (
    <div className="center-state">Página não encontrada.</div>
  ),
});

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  validateSearch: (search: Record<string, unknown>): { session?: "expired" } =>
    search.session === "expired" ? { session: "expired" } : {},
  beforeLoad: async ({ search }) => {
    // An invalid session may still be in storage while local sign-out finishes.
    // Do not wait on Auth's lock or bounce the recovery screen back into the app.
    if (search.session === "expired") return;
    const { data } = await supabase.auth.getSession();
    if (data.session) throw redirect({ to: "/sinais" });
  },
  component: lazyRouteComponent(
    () => import("@/features/auth/login-page"),
    "LoginPage",
  ),
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/redefinir-senha",
  component: lazyRouteComponent(
    () => import("@/features/auth/reset-password-page"),
    "ResetPasswordPage",
  ),
});

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw redirect({ to: "/auth" });
  },
  component: lazyRouteComponent(() => import("@/app/app-shell"), "AppShell"),
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/sinais" });
  },
});

const submissionRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/enviar",
  component: lazyRouteComponent(
    () => import("@/features/submissions/submission-form-page"),
    "SubmissionFormPage",
  ),
});

const teamRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/equipe",
  component: lazyRouteComponent(
    () => import("@/features/team/team-page"),
    "TeamPage",
  ),
});

const memberDirectoryRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/pessoas",
  component: lazyRouteComponent(
    () => import("@/features/team/permission-read-pages"),
    "MemberDirectoryPage",
  ),
});
const aiUsageRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/uso-ia",
  component: lazyRouteComponent(
    () => import("@/features/team/permission-read-pages"),
    "AiUsagePage",
  ),
});

const managementRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/gestao",
  component: lazyRouteComponent(
    () => import("@/features/management/gestao-page"),
    "GestaoPage",
  ),
});

const catalogRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/catalogo",
  component: lazyRouteComponent(
    () => import("@/features/catalog/catalog-page"),
    "CatalogPage",
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/configuracoes",
  component: lazyRouteComponent(
    () => import("@/features/settings/settings-page"),
    "SettingsPage",
  ),
});

const catalogRequestsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/solicitacoes",
  component: lazyRouteComponent(
    () => import("@/features/catalog/catalog-requests-page"),
    "CatalogRequestsPage",
  ),
});

const signalsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/sinais",
  component: lazyRouteComponent(
    () => import("@/features/submissions/submissions-page"),
    "SubmissionsPage",
  ),
});
const commercialRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/comercial",
  component: lazyRouteComponent(
    () => import("@/features/analytics/commercial-page"),
    "CommercialPage",
  ),
});
const leaderboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/gamificacao",
  component: lazyRouteComponent(
    () => import("@/features/analytics/leaderboard-page"),
    "LeaderboardPage",
  ),
});
const insightsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/insights",
  component: lazyRouteComponent(
    () => import("@/features/insights/insights-page"),
    "InsightsPage",
  ),
});
const actionsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/acoes",
  component: lazyRouteComponent(
    () => import("@/features/actions/actions-page"),
    "ActionsPage",
  ),
});
const drilldownRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/drilldown",
  component: lazyRouteComponent(
    () => import("@/features/analytics/drilldown-page"),
    "DrilldownPage",
  ),
});

const legacySubmitRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/submit",
  beforeLoad: () => {
    throw redirect({ to: "/enviar" });
  },
});
const legacyDashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/dashboard",
  beforeLoad: () => {
    throw redirect({ to: "/sinais" });
  },
});
const legacyDashboardsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/dashboards",
  beforeLoad: () => {
    throw redirect({ to: "/comercial" });
  },
});
const legacyGamificationRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/gamification",
  beforeLoad: () => {
    throw redirect({ to: "/gamificacao" });
  },
});
const legacyResetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  beforeLoad: () => {
    throw redirect({ to: "/redefinir-senha" });
  },
});

const routeTree = rootRoute.addChildren([
  authRoute,
  resetPasswordRoute,
  legacyResetRoute,
  authenticatedRoute.addChildren([
    dashboardRoute,
    submissionRoute,
    catalogRequestsRoute,
    catalogRoute,
    settingsRoute,
    teamRoute,
    memberDirectoryRoute,
    aiUsageRoute,
    managementRoute,
    signalsRoute,
    commercialRoute,
    leaderboardRoute,
    insightsRoute,
    drilldownRoute,
    actionsRoute,
    legacySubmitRoute,
    legacyDashboardRoute,
    legacyDashboardsRoute,
    legacyGamificationRoute,
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
